import { z } from "zod";

/**
 * Create Slack App Request Body
 *
 * Used to programmatically create a Slack app via Manifest API.
 *
 * Either provide:
 * - workspaceConnectionId (tokens fetched from stored connection)
 * - OR configToken + refreshToken (direct tokens)
 */
export const CreateSlackAppBodySchema = z
  .object({
    /** Deployment ID to create the app for */
    deploymentId: z.string().uuid(),
    /** Workspace connection ID (alternative to raw tokens) */
    workspaceConnectionId: z.string().uuid().optional(),
    /** App configuration token from api.slack.com/apps "App Configuration Tokens" */
    configToken: z.string().min(1).optional(),
    /** Refresh token for renewing config token */
    refreshToken: z.string().min(1).optional(),
    /** Admin token for Enterprise Grid auto-install (optional) */
    adminToken: z.string().min(1).optional(),
    /** Custom bot name (optional, defaults to agent name) */
    botName: z.string().trim().min(1).max(80).optional(),
    /** Custom short description (optional, defaults to agent short_bio, max 100 chars) */
    description: z.string().max(100).optional()
  })
  .refine(
    (data) => {
      // workspaceConnectionId OR both tokens OR neither (IT approval retry — backend resolves by teamId)
      if (data.workspaceConnectionId) return true;
      if (data.configToken && data.refreshToken) return true;
      // No tokens at all: allowed for IT approval retry flow
      if (!data.configToken && !data.refreshToken) return true;
      // Partial tokens (one without the other): invalid
      return false;
    },
    {
      message: "Provide either workspaceConnectionId, or both configToken and refreshToken"
    }
  );

export type CreateSlackAppBody = z.infer<typeof CreateSlackAppBodySchema>;

/**
 * Create Slack App Response
 *
 * Response varies based on installation type:
 * - "oauth": Regular workspace, user must approve via oauthUrl
 * - "auto": Enterprise Grid, app was auto-installed, connectionId returned
 *
 * Note: success is handled by ApiSuccessSchema wrapper, not here
 */
export const CreateSlackAppResponseSchema = z.object({
  /** App ID created in Slack */
  appId: z.string(),
  /** Installation type: oauth (user approval needed) or auto (Enterprise Grid) */
  installType: z.enum(["oauth", "auto"]),
  /** URL for user to authorize (oauth flow only) */
  oauthUrl: z.string().optional(),
  /** Handshake ID for tracking this flow (oauth flow only) */
  handshakeId: z.string().uuid().optional(),
  /** Connection ID (auto-install, or returned after oauth completion) */
  connectionId: z.string().uuid().optional(),
  /** Team name (both flows - for display) */
  teamName: z.string().optional()
});

export type CreateSlackAppResponse = z.infer<typeof CreateSlackAppResponseSchema>;
